"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  LABEL,
  TEXTAREA_PROSE,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { graphemeLength } from "@/lib/graphemes";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { randomIndex, secureRandom } from "@/lib/tool-kit/classroom/random";
import { REPORT_COMMENT_BANK } from "@/lib/tool-kit/classroom/report-comment-bank";
import {
  assembleComments,
  bankKey,
  COMMENT_CATEGORIES,
  type CommentCategory,
  type CommentLevel,
  type Pronoun,
  renderComment,
} from "@/lib/tool-kit/classroom/report-comments";

const WORDS = /\S+/g;

/** English strings. A translated content file overrides these through `ui`. */
export const REPORT_CARD_COMMENT_GENERATOR_STRINGS = {
  nameLabel: "Student's first name",
  namePlaceholder: "Jordan",
  nameFallback: "This student",
  pronounLabel: "Pronouns",
  pronouns: { they: "they/them", she: "she/her", he: "he/him" },
  pronounForms: {
    they: ["they", "them", "their", "themselves"],
    she: ["she", "her", "her", "herself"],
    he: ["he", "him", "his", "himself"],
  },
  subjectLabel: "Subject (optional)",
  subjectPlaceholder: "math",
  subjectFallback: "class",
  categoriesLabel: "Categories and levels",
  categoryNames: {
    achievement: "Achievement",
    effort: "Effort",
    participation: "Participation",
    behavior: "Behavior",
    growth: "Areas for growth",
    "next-steps": "Next steps",
  },
  levels: {
    skip: "Leave out",
    excellent: "Excellent",
    satisfactory: "Satisfactory",
    "needs-improvement": "Needs improvement",
  },
  chooseFor: "{category} — {level}",
  shuffle: "Shuffle picks",
  nameEvery: "Start every comment with the student's name",
  outputLabel: "Report card comment",
  edited:
    "You have edited the comment, so changes to the choices above are not applied. Rebuild to start again from your selections.",
  rebuild: "Rebuild from selections",
  characters: "one:{count} character|other:{count} characters",
  words: "one:{count} word|other:{count} words",
  copy: "Copy comment",
  copied: "Copied!",
  empty: "Choose a level for at least one category to build a comment.",
  toneHeading: "Tone guidance",
  toneTips: [
    "Open with a genuine strength before any area for growth.",
    "Be specific: name the skill, the evidence you saw, and one next step.",
    'Describe work and behavior, not personality — "is not yet completing homework", not "is lazy".',
    'Use growth language for concerns: "not yet", "is developing", "with practice".',
    "Keep it consistent with the grade and with what families have already heard from you.",
    "Read it aloud: would you be comfortable saying it at a family conference?",
  ],
  bank: REPORT_COMMENT_BANK,
} as const;

type Strings = typeof REPORT_CARD_COMMENT_GENERATOR_STRINGS;
type Choice = CommentLevel | "skip";

const LEVEL_CHOICES: readonly Choice[] = [
  "skip",
  "excellent",
  "satisfactory",
  "needs-improvement",
];

function CategoryPicker({
  category,
  level,
  picked,
  options,
  strings,
  onLevel,
  onPick,
}: {
  category: CommentCategory;
  level: Choice;
  picked: number;
  options: string[];
  strings: Strings;
  onLevel: (level: Choice) => void;
  onPick: (index: number) => void;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <SelectField
        id={`rc-level-${category}`}
        label={strings.categoryNames[category]}
        onChange={onLevel}
        options={LEVEL_CHOICES.map((value) => ({
          value,
          label: strings.levels[value],
        }))}
        value={level}
      />
      {level === "skip" ? null : (
        <fieldset className="space-y-2">
          <legend className="sr-only">
            {t(strings.chooseFor, {
              category: strings.categoryNames[category],
              level: strings.levels[level],
            })}
          </legend>
          {options.map((option, index) => (
            <label
              className="flex cursor-pointer items-start gap-2 text-foreground text-sm leading-relaxed"
              key={option}
            >
              <input
                checked={picked === index}
                className="mt-1 accent-primary"
                name={`rc-pick-${category}`}
                onChange={() => onPick(index)}
                type="radio"
              />
              <span>{option}</span>
            </label>
          ))}
        </fieldset>
      )}
    </div>
  );
}

export default function ReportCardCommentGeneratorWidget() {
  const strings = useToolUi(REPORT_CARD_COMMENT_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [name, setName] = useState("");
  const [pronoun, setPronoun] = useState<Pronoun>("they");
  const [subject, setSubject] = useState("");
  const [levels, setLevels] = useState<Record<CommentCategory, Choice>>({
    achievement: "satisfactory",
    effort: "excellent",
    participation: "skip",
    behavior: "skip",
    growth: "satisfactory",
    "next-steps": "satisfactory",
  });
  const [picks, setPicks] = useState<Record<CommentCategory, number>>({
    achievement: 0,
    effort: 0,
    participation: 0,
    behavior: 0,
    growth: 0,
    "next-steps": 0,
  });
  const [nameEvery, setNameEvery] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);

  const context = {
    name,
    nameFallback: strings.nameFallback,
    pronoun,
    forms: strings.pronounForms[pronoun],
    subject,
    subjectFallback: strings.subjectFallback,
    locale,
  };
  const templatesFor = (category: CommentCategory, level: Choice): string[] =>
    level === "skip" ? [] : (strings.bank[bankKey(category, level)] ?? []);
  const chosen = COMMENT_CATEGORIES.flatMap((category) => {
    const options = templatesFor(category, levels[category]);
    const template = options[picks[category]] ?? options[0];
    return template ? [template] : [];
  });
  const assembled = assembleComments(chosen, context, nameEvery);
  const text = draft ?? assembled;
  const characters = graphemeLength(text, locale);
  const words = text.match(WORDS)?.length ?? 0;

  function shuffle() {
    setPicks((previous) => {
      const next = { ...previous };
      for (const category of COMMENT_CATEGORIES) {
        const count = templatesFor(category, levels[category]).length;
        next[category] = count > 0 ? randomIndex(count, secureRandom) : 0;
      }
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="rc-name"
          label={strings.nameLabel}
          onChange={setName}
          placeholder={strings.namePlaceholder}
          value={name}
        />
        <TextField
          id="rc-subject"
          label={strings.subjectLabel}
          onChange={setSubject}
          placeholder={strings.subjectPlaceholder}
          value={subject}
        />
      </div>
      <PillGroup
        label={strings.pronounLabel}
        name="rc-pronoun"
        onChange={setPronoun}
        options={(["they", "she", "he"] as const).map((value) => ({
          value,
          label: strings.pronouns[value],
        }))}
        value={pronoun}
      />
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.categoriesLabel}</h2>
          <button className={BUTTON_SMALL} onClick={shuffle} type="button">
            {strings.shuffle}
          </button>
        </div>
        <div className="grid gap-3">
          {COMMENT_CATEGORIES.map((category) => (
            <CategoryPicker
              category={category}
              key={category}
              level={levels[category]}
              onLevel={(level) => {
                setLevels((previous) => ({ ...previous, [category]: level }));
                setPicks((previous) => ({ ...previous, [category]: 0 }));
              }}
              onPick={(index) =>
                setPicks((previous) => ({ ...previous, [category]: index }))
              }
              options={templatesFor(category, levels[category]).map(
                (template) =>
                  renderComment(template, { ...context, useName: true })
              )}
              picked={picks[category]}
              strings={strings}
            />
          ))}
        </div>
        <Checkbox
          checked={nameEvery}
          label={strings.nameEvery}
          onChange={setNameEvery}
        />
      </section>

      <section className="space-y-3">
        <label className={LABEL} htmlFor="rc-output">
          {strings.outputLabel}
        </label>
        {chosen.length === 0 && draft === null ? (
          <Notice>{strings.empty}</Notice>
        ) : (
          <textarea
            className={TEXTAREA_PROSE}
            id="rc-output"
            onChange={(event) => setDraft(event.target.value)}
            value={text}
          />
        )}
        <p className={HINT}>
          {tPlural(strings.characters, locale, characters, {
            count: fmt(characters),
          })}{" "}
          · {tPlural(strings.words, locale, words, { count: fmt(words) })}
        </p>
        <div aria-live="polite">
          {draft !== null && draft !== assembled ? (
            <Notice>{strings.edited}</Notice>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copy}
            text={text}
          />
          {draft === null ? null : (
            <button
              className={BUTTON_SECONDARY}
              onClick={() => setDraft(null)}
              type="button"
            >
              {strings.rebuild}
            </button>
          )}
        </div>
      </section>

      <aside className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.toneHeading}</h3>
        <ul className="list-disc space-y-1 ps-5 text-muted-foreground text-sm leading-relaxed">
          {strings.toneTips.map((tip) => (
            <li key={tip}>{tip}</li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
